import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function ResetPassword() {
  const { t } = useLang();
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) setError(t("resetLinkInvalid"));
  }, [token, t]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError(t("passwordTooShort"));
      return;
    }
    if (password !== confirm) {
      setError(t("passwordsDontMatch"));
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password });
      setUser(data);
      toast.success(t("passwordUpdated"));
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="font-heading text-5xl font-medium text-stone-900">
            Julia<span className="italic text-[#9F8E7E]"> AI</span>
          </h1>
        </div>
        <div className="bg-white border border-stone-200 rounded-2xl p-8">
          <h2 className="font-heading text-2xl font-medium text-stone-900 mb-1">{t("resetTitle")}</h2>
          <p className="text-sm text-stone-500 mb-6">{t("resetSubtitle")}</p>

          <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
            <div className="space-y-1.5">
              <Label htmlFor="reset-password">{t("newPassword")}</Label>
              <Input id="reset-password" data-testid="reset-password" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
              <p className="text-xs text-stone-400">{t("passwordHint")}</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="reset-confirm">{t("confirmPassword")}</Label>
              <Input id="reset-confirm" data-testid="reset-confirm" type="password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" />
            </div>
            {error && <p className="text-sm text-red-600" data-testid="reset-error">{error}</p>}
            <Button type="submit" data-testid="reset-submit" disabled={loading || !token} className="w-full bg-stone-900 hover:bg-stone-800 text-white">
              {loading ? t("updating") : t("updatePassword")}
            </Button>
            <button type="button" onClick={() => navigate("/login")} className="w-full text-sm text-stone-500 hover:text-stone-900" data-testid="reset-back-to-signin">
              {t("backToSignIn")}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
